import { Component, input, inject, computed } from '@angular/core';
import { IFilterComponent, IFilter, IFilterItem } from '../interfaces';
import { BaseComponent } from '@nha/components/base.component';
import { arraysEqual } from '@nha/functions/array-are-equal.function';
import { FilterStateService } from '../state/filter-state.service';

@Component({
	selector: 'nha-filter-base',
	template: ''
})
export abstract class FilterBaseComponent extends BaseComponent implements IFilterComponent {
	protected readonly filterState = inject(FilterStateService);

	key = input.required<string>();
	filter = input.required<IFilter>();
	filterKey = input.required<string>();
	counts = input<boolean>(false);

	protected filterInstance = computed(() => this.filterState.getFilterInstance(this.key())());

	protected locked = computed(() => this.filterInstance().config.locked);

	/**
	 * Badge state for this filter's items, resolved from the service rather than read off
	 * the items themselves — see FilterStateService.getFilterCounts. `null` means suppress.
	 */
	protected itemCounts = computed(() => this.filterState.getFilterCounts(this.key(), this.filterKey())());

	constructor() {
		super();
	}

	select(filterKey: string, filter: IFilter, selectedID: number): void {
		if (this.locked()) {
			return;
		}

		// Resolved from the service, not from the item: `disabled` is derived badge state and
		// no longer lives on IFilterItem. Read against the passed filterKey rather than
		// this.filterKey() because IFilterComponent.select takes it as an argument.
		if (this.filterState.getFilterCounts(this.key(), filterKey)()?.get(selectedID)?.disabled) {
			return;
		}

		const currentSelectedItems = filter.items.filter((i) => i.selected).map((i) => i.id);

		if (filter.multiple) {
			const updatedItems = filter.items.map((item) => {
				if (item.id === selectedID) {
					return { ...item, selected: !item.selected };
				}
				return item;
			});

			const newValueItems = updatedItems.filter((i) => i.selected).map((i) => i.id);

			if (!arraysEqual(currentSelectedItems, newValueItems)) {
				const updatedFilter = { ...filter, items: updatedItems };
				this.filterState.updateFilter(this.key(), filterKey, updatedFilter);
			}
		} else {
			let deselect = false;

			if (currentSelectedItems[0] === selectedID) {
				deselect = true;
			}

			const updatedItems = filter.items.map((item) => ({
				...item,
				selected: deselect ? false : item.id === selectedID
			}));

			const updatedFilter = { ...filter, items: updatedItems };
			this.filterState.updateFilter(this.key(), filterKey, updatedFilter);
		}
	}

	/** The count to badge this item with, or `null` when no badge should render. */
	countFor(item: IFilterItem): number | null {
		return this.itemCounts()?.get(item.id)?.count ?? null;
	}

	disabledFor(item: IFilterItem): boolean {
		return this.itemCounts()?.get(item.id)?.disabled ?? false;
	}

	itemAriaLabel(item: IFilterItem, count: number | null): string {
		return count === null || count === undefined ? `${item.label}` : `${item.label}, ${count} results`;
	}
}
